import { Subscription, interval, startWith, take } from 'rxjs';

export class Countdown {
  private subscription: Subscription | undefined;

  start(endDate: number, updateCallback: (countDown: number, finished: boolean) => void): void {
    const milliseconds = endDate;
    if (milliseconds) {
      const currentDate = new Date();
      const futureDate = new Date(milliseconds);

      if (futureDate > currentDate) {
        const timeDifference = futureDate.getTime() - currentDate.getTime();
        const totalSeconds = Math.floor(timeDifference / 1000);
        this.subscription?.unsubscribe();
        updateCallback(totalSeconds, false);
        this.subscription = interval(1000)
          .pipe(startWith(0), take(totalSeconds))
          .subscribe((secondsPassed) => {
            const ended = secondsPassed >= totalSeconds;
            updateCallback(totalSeconds - secondsPassed, ended);
            if (ended) {
              this.subscription?.unsubscribe();
            }
          });
      } else {
        updateCallback(endDate, true);
      }
    }
  }

  stop(): void {
    this.subscription?.unsubscribe();
  }

  static asTimeLeft(remainingSeconds: number): string {
    if (remainingSeconds <= 0) {
      return '0m 0s';
    }

    const hours = Math.floor(remainingSeconds / 3600);
    const minutes = Math.floor((remainingSeconds % 3600) / 60);
    const seconds = remainingSeconds % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m ${seconds}s`;
    } else {
      return `${minutes}m ${seconds}s`;
    }
  }

  static asDate(unixTime: number): string {
    const date = new Date(unixTime);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear().toString().slice(-2);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  }
}

export function isNullOrUndefined(value: unknown): value is null | undefined {
  return value === null || value === undefined;
}

export function isNotNullOrUndefined(value: unknown): value is null | undefined {
  return !isNullOrUndefined(value);
}

export function isNil<T>(value: T | null | undefined): value is T {
  return value === null || value === undefined;
}

export function isNotNil<T>(value: T | null | undefined): value is T {
  return value !== null && value !== undefined;
}
